import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../../services/supportService';
import { TicketCategory, TicketStatus, SupportTicketDetail } from '../../types/support';
import { useAuthStore } from '../../stores/authStore';

const statusColor = (s: TicketStatus) => {
  switch (s) {
    case TicketStatus.OPEN: return 'warning';
    case TicketStatus.IN_PROGRESS: return 'info';
    case TicketStatus.RESOLVED: return 'success';
    case TicketStatus.CLOSED: return 'default';
  }
};

export const SupportPage = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketDetail | null>(null);
  const [reply, setReply] = useState('');

  // Create form state
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<TicketCategory>(TicketCategory.GENERAL);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['support-tickets'],
    queryFn: () => supportService.listMyTickets(),
    enabled: view === 'list',
  });

  const { data: ticketDetail, isLoading: loadingDetail, refetch: refetchDetail } = useQuery({
    queryKey: ['support-ticket', selectedTicket?.id],
    queryFn: () => supportService.getTicket(selectedTicket!.id),
    enabled: view === 'detail' && !!selectedTicket,
  });

  const createMutation = useMutation({
    mutationFn: () => supportService.createTicket({ subject, category, message }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-tickets'] });
      setSubject(''); setCategory(TicketCategory.GENERAL); setMessage(''); setFormError('');
      setView('list');
    },
    onError: () => setFormError(t('support.createError')),
  });

  const replyMutation = useMutation({
    mutationFn: () => supportService.replyToTicket(selectedTicket!.id, reply),
    onSuccess: () => {
      setReply('');
      refetchDetail();
      qc.invalidateQueries({ queryKey: ['support-tickets'] });
    },
  });

  const handleCreate = () => {
    if (!subject.trim() || !message.trim()) {
      setFormError(t('support.fillFields'));
      return;
    }
    createMutation.mutate();
  };

  const handleOpenTicket = (ticket: SupportTicketDetail) => {
    setSelectedTicket(ticket);
    setView('detail');
  };

  const displayTicket = ticketDetail ?? selectedTicket;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 2 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('support.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('support.subtitle')}
      </Typography>

      {/* List view */}
      {view === 'list' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setView('create')}>
              {t('support.newTicket')}
            </Button>
          </Box>

          {isLoading && <CircularProgress />}

          {!isLoading && (!tickets || tickets.length === 0) && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('support.noTickets')}</Typography>
            </Paper>
          )}

          {tickets?.map((ticket) => (
            <Paper
              key={ticket.id}
              sx={{ p: 2, mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => handleOpenTicket(ticket as SupportTicketDetail)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography fontWeight={600}>{ticket.subject}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(`support.categories.${ticket.category}`)} &middot;{' '}
                    {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                    {ticket.message_count > 0 && ` · ${ticket.message_count} ${t('support.messages')}`}
                  </Typography>
                </Box>
                <Chip
                  label={t(`support.statuses.${ticket.status}`)}
                  color={statusColor(ticket.status)}
                  size="small"
                />
              </Box>
            </Paper>
          ))}
        </>
      )}

      {/* Create view */}
      {view === 'create' && (
        <Paper sx={{ p: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setView('list')} sx={{ mb: 2 }}>
            {t('common.back')}
          </Button>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('support.newTicket')}
          </Typography>

          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <TextField
            label={t('support.subject')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 200 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('support.category')}</InputLabel>
            <Select
              value={category}
              label={t('support.category')}
              onChange={(e) => setCategory(e.target.value as TicketCategory)}
            >
              {Object.values(TicketCategory).map((c) => (
                <MenuItem key={c} value={c}>{t(`support.categories.${c}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('support.message')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            rows={5}
            sx={{ mb: 3 }}
            inputProps={{ maxLength: 5000 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setView('list')}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={createMutation.isPending}
              startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {t('support.submit')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Detail view */}
      {view === 'detail' && displayTicket && (
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => { setView('list'); setSelectedTicket(null); }} sx={{ mb: 2 }}>
            {t('common.back')}
          </Button>

          <Paper sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>{displayTicket.subject}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`support.categories.${displayTicket.category}`)} &middot;{' '}
                  {new Date(displayTicket.created_at).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
              <Chip
                label={t(`support.statuses.${displayTicket.status}`)}
                color={statusColor(displayTicket.status)}
              />
            </Box>
          </Paper>

          {loadingDetail && <CircularProgress />}

          {/* Messages thread */}
          <Box sx={{ mb: 2 }}>
            {displayTicket.messages?.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: msg.is_admin_reply ? 'flex-start' : 'flex-end',
                  mb: 1.5,
                }}
              >
                <Paper
                  sx={{
                    p: 2,
                    maxWidth: '75%',
                    bgcolor: msg.is_admin_reply ? 'primary.light' : 'grey.100',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color={msg.is_admin_reply ? 'primary.dark' : 'text.secondary'}>
                    {msg.is_admin_reply ? t('support.supportTeam') : (msg.sender_name ?? t('support.you'))}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {new Date(msg.created_at).toLocaleString('fr-FR')}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>

          {/* Reply box */}
          {displayTicket.status !== TicketStatus.CLOSED && (
            <Paper sx={{ p: 2 }}>
              <TextField
                placeholder={t('support.replyPlaceholder')}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={() => replyMutation.mutate()}
                  disabled={!reply.trim() || replyMutation.isPending}
                >
                  {t('support.send')}
                </Button>
              </Box>
            </Paper>
          )}
          {displayTicket.status === TicketStatus.CLOSED && (
            <Alert severity="info">{t('support.ticketClosed')}</Alert>
          )}
        </Box>
      )}
    </Box>
  );
};
